import React, { useCallback, useContext, useMemo } from 'react';
import { useCurrentFrame, useVideoConfig } from 'remotion';
import { retryAssetUpload } from '../assets/retry-upload';
import {
  ItemSelectedForCropContext,
  TextItemEditingContext,
  TextItemHoverPreviewContext,
} from '../context-provider';
import { EditModeContext } from '../edit-mode';
import {
  FEATURE_CROPPING,
  FEATURE_DOUBLE_CLICK_TO_CROP,
  FEATURE_SHIFT_AXIS_LOCK,
} from '../flags';
import { calculateAnimationsValues } from '../items/animation/calculate-animation';
import type { ItemAnimation } from '../items/animation/animation-item-type';
import {
  getPositionOffsetAt,
  upsertPositionKeyframe,
} from '../items/animation/position-keyframes';
import { resolveGroupGeometry } from '../items/group/group-geometry';
import { EditorStarterItem } from '../items/item-type';
import { overrideItemWithHoverPreview } from '../items/override-item-with-hover-preview';
import { calculateMoveSnap, useSmartGuidesOptional } from '../smart-guides';
import type { SnapTargetRect } from '../smart-guides';
import { changeItem } from '../state/actions/change-item';
import { selectItemForCrop } from '../state/actions/item-cropping';
import { setActiveSelectionSurface } from '../state/actions/set-active-selection-surface';
import { setSelectedItems } from '../state/actions/set-selected-items';
import {
  selectGroup,
  enterGroupMember,
  collectGroupMemberIds,
} from '../state/actions/select-group';
import { hitTestGroupMember } from '../items/group/hit-test-group-member';
import { markTextAsEditing } from '../state/actions/text-item-editing';
import { ItemContextMenuTrigger } from '../timeline/timeline-item/timeline-item-context-menu-trigger';
import {
  canAssetRetryUpload,
  isAssetError,
  isAssetUploading,
} from '../utils/asset-status-helpers';
import { useCanvasTransformationScale } from '../utils/canvas-transformation-context';
import { getCanCrop, getCropFromItem } from '../utils/get-crop-from-item';
import { getRectAfterCrop } from '../utils/get-dimensions-after-crop';
import { isLeftClick } from '../utils/is-left-click';
import {
  useAssetIfApplicable,
  useAssetStatus,
  useAllItems,
  useCurrentStateAsRef,
  useDimensions,
  useSelectedItems,
  useTracks,
  useWriteContext,
} from '../utils/use-context';
import { CropButton } from './crop-button';
import { CropHandle } from './crop-handle';
import { CropSourceScaleHandle } from './crop-source-scale-handle';
import { ResizeHandle } from './resize-handle';
import { SelectionDimensions } from './selection-dimensions';
import { SelectionError } from './selection-upload-error';
import { SelectionUploadProgress } from './selection-upload-progress';
import {
  getNextCodeFieldSelection,
  getPrimaryCodeFieldKey,
  normalizeCodeFieldSelection,
} from './code-field-selection';
import {
  getActiveCodeEditableFields,
  updateCodeEditableField,
  updateCodeEditableFieldGeometry,
} from '@/features/remotion-editor/editor/code-data-editing';
import type {
  CodeEditableField,
  InlineEditStyle,
  ItemGeometry,
} from '@/features/remotion-editor/editor/code-data-editing';
import { isTextItem } from '@/features/remotion-editor/editor/items/text/text-item-type';

const AXIS_LOCK_THRESHOLD = 10; // 축을 결정하기 위한 최소 이동 거리
const CODE_FIELD_MIN_SIZE = 24;
const CODE_FIELD_HANDLE_TYPES = [
  'top-left',
  'top-right',
  'bottom-left',
  'bottom-right',
  'top',
  'right',
  'bottom',
  'left',
] as const;

type CodeFieldResizeHandleType = (typeof CODE_FIELD_HANDLE_TYPES)[number];

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const geometryChanged = (from: ItemGeometry, to: ItemGeometry) =>
  Math.abs(from.left - to.left) > 0.5 ||
  Math.abs(from.top - to.top) > 0.5 ||
  Math.abs(from.width - to.width) > 0.5 ||
  Math.abs(from.height - to.height) > 0.5;

const getCodeActiveFieldKeys = (item: EditorStarterItem) => {
  if (item.type !== 'code') {
    return [];
  }

  const fieldKeys = Array.isArray(item.codeActiveFields)
    ? normalizeCodeFieldSelection(item.codeActiveFields)
    : [];

  if (fieldKeys.length > 0) {
    return fieldKeys;
  }

  const legacyFieldKey = item.codeActiveField ?? item.shinhanPdfActiveField;

  return legacyFieldKey ? [legacyFieldKey] : [];
};

const toInlineEditCssProperties = (
  style?: InlineEditStyle,
): React.CSSProperties => (style ? (style as React.CSSProperties) : {});

const getEditableText = (element: HTMLElement | null) =>
  element?.textContent ?? '';

const clampCodeFieldGeometry = (
  geometry: ItemGeometry,
  compositionWidth: number,
  compositionHeight: number,
): ItemGeometry => {
  const width = clamp(
    Math.round(geometry.width),
    CODE_FIELD_MIN_SIZE,
    compositionWidth,
  );
  const height = clamp(
    Math.round(geometry.height),
    CODE_FIELD_MIN_SIZE,
    compositionHeight,
  );

  return {
    left: clamp(Math.round(geometry.left), 0, compositionWidth - width),
    top: clamp(Math.round(geometry.top), 0, compositionHeight - height),
    width,
    height,
  };
};

const resizeCodeFieldGeometry = ({
  type,
  geometry,
  offsetX,
  offsetY,
  compositionWidth,
  compositionHeight,
}: {
  type: CodeFieldResizeHandleType;
  geometry: ItemGeometry;
  offsetX: number;
  offsetY: number;
  compositionWidth: number;
  compositionHeight: number;
}): ItemGeometry => {
  let { left, top, width, height } = geometry;

  if (type.includes('left')) {
    left += offsetX;
    width -= offsetX;
  }
  if (type.includes('right')) {
    width += offsetX;
  }
  if (type.includes('top')) {
    top += offsetY;
    height -= offsetY;
  }
  if (type.includes('bottom')) {
    height += offsetY;
  }

  if (width < CODE_FIELD_MIN_SIZE) {
    if (type.includes('left')) {
      left -= CODE_FIELD_MIN_SIZE - width;
    }
    width = CODE_FIELD_MIN_SIZE;
  }

  if (height < CODE_FIELD_MIN_SIZE) {
    if (type.includes('top')) {
      top -= CODE_FIELD_MIN_SIZE - height;
    }
    height = CODE_FIELD_MIN_SIZE;
  }

  return clampCodeFieldGeometry(
    { left, top, width, height },
    compositionWidth,
    compositionHeight,
  );
};

const codeFieldHandleStyle = (
  type: CodeFieldResizeHandleType,
  size: number,
): React.CSSProperties => {
  const half = -size / 2;
  const base: React.CSSProperties = {
    position: 'absolute',
    width: size,
    height: size,
    background: 'var(--sys-bg-surface)',
    border: '1px solid var(--color-editor-starter-accent)',
    borderRadius: 2,
    zIndex: 3,
  };

  if (type === 'top-left') {
    return { ...base, left: half, top: half, cursor: 'nwse-resize' };
  }
  if (type === 'top-right') {
    return { ...base, right: half, top: half, cursor: 'nesw-resize' };
  }
  if (type === 'bottom-left') {
    return { ...base, left: half, bottom: half, cursor: 'nesw-resize' };
  }
  if (type === 'bottom-right') {
    return { ...base, right: half, bottom: half, cursor: 'nwse-resize' };
  }
  if (type === 'top') {
    return {
      ...base,
      left: '50%',
      top: half,
      marginLeft: half,
      cursor: 'ns-resize',
    };
  }
  if (type === 'right') {
    return {
      ...base,
      right: half,
      top: '50%',
      marginTop: half,
      cursor: 'ew-resize',
    };
  }
  if (type === 'bottom') {
    return {
      ...base,
      left: '50%',
      bottom: half,
      marginLeft: half,
      cursor: 'ns-resize',
    };
  }

  return {
    ...base,
    left: half,
    top: '50%',
    marginTop: half,
    cursor: 'ew-resize',
  };
};

const CodeFieldInlineEditor: React.FC<{
  inlineEditStyle?: InlineEditStyle;
  value: string;
  onCancel: () => void;
  onCommit: (value: string) => void;
}> = ({ inlineEditStyle, onCancel, onCommit, value }) => {
  const editorRef = React.useRef<HTMLDivElement | null>(null);
  const skipCommitRef = React.useRef(false);

  React.useEffect(() => {
    const element = editorRef.current;

    if (!element) {
      return;
    }

    element.focus();

    const selection = window.getSelection();
    const range = document.createRange();

    range.selectNodeContents(element);
    range.collapse(false);
    selection?.removeAllRanges();
    selection?.addRange(range);
  }, []);

  return (
    <div
      ref={editorRef}
      contentEditable
      suppressContentEditableWarning
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
      onDoubleClick={(event) => event.stopPropagation()}
      onBlur={(event) => {
        if (skipCommitRef.current) {
          skipCommitRef.current = false;
          return;
        }

        onCommit(getEditableText(event.currentTarget));
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          skipCommitRef.current = true;
          onCancel();
          return;
        }

        if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault();
          event.currentTarget.blur();
        }
      }}
      style={{
        ...toInlineEditCssProperties(inlineEditStyle),
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        display: 'block',
        zIndex: 4,
        cursor: 'text',
        pointerEvents: 'auto',
        userSelect: 'text',
      }}
    >
      {value}
    </div>
  );
};

const SelectionOutlineUnmemoized: React.FC<{
  item: EditorStarterItem;
  hoveredGroupId?: string | null;
  onHoverGroupChange?: (groupId: string | null) => void;
  /**
   * 렌더 단계. 선택된 아이템의 핸들·크롭 버튼을 겹친 다른 아이템 위에서
   * 클릭되게 하기 위해 2-pass 로 나눈다.
   * - 'all'(기본): outline div + 핸들 레이어 (비선택 아이템)
   * - 'base': outline div 만 (핸들 레이어는 최상단 pass 에서 렌더)
   * - 'handles': 핸들 레이어만 (DOM 최후미 → 겹친 아이템 위에서 클릭됨)
   */
  phase?: 'all' | 'base' | 'handles';
}> = ({
  item: itemWithoutHoverPreview,
  hoveredGroupId = null,
  onHoverGroupChange,
  phase = 'all',
}) => {
  const scale = useCanvasTransformationScale();
  const currentFrame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const textItemHoverPreview = useContext(TextItemHoverPreviewContext);
  const { compositionWidth, compositionHeight } = useDimensions();
  const smartGuides = useSmartGuidesOptional();
  const { editMode } = useContext(EditModeContext);

  const item = useMemo(
    () =>
      overrideItemWithHoverPreview({
        item: itemWithoutHoverPreview,
        hoverPreview: textItemHoverPreview,
      }),
    [itemWithoutHoverPreview, textItemHoverPreview],
  );

  const { selectedItems, activeGroupId } = useSelectedItems();
  const { items: allItems } = useAllItems();
  const { setState } = useWriteContext();
  const { assetStatus } = useAssetStatus();
  const stateAsRef = useCurrentStateAsRef();
  const textItemEditing = useContext(TextItemEditingContext);
  const itemSelectedForCrop = useContext(ItemSelectedForCropContext);
  const { tracks } = useTracks();

  const itemIsBeingTextEdited =
    item.type === 'text' && item.id === textItemEditing;
  const itemIsBeingCropped = item.id === itemSelectedForCrop;

  const border = itemIsBeingCropped ? 1 : 2;
  const scaledBorder = Math.ceil(border / scale);

  const isLocked = useMemo(() => {
    const track = tracks.find((t) => t.items.includes(item.id));
    return track?.locked ?? false;
  }, [tracks, item.id]);

  // 메인 트랙에 속하지 않은 이미지/영상 아이템인지 확인
  const isNonMainTrackImageOrVideo = useMemo(() => {
    const track = tracks.find((t) => t.items.includes(item.id));
    const isMainTrack =
      track?.metadata?.isMainTrack === true ||
      track?.metadata?.trackType === 'main' ||
      item.isMainTrack === true;
    return (item.type === 'image' || item.type === 'video') && !isMainTrack;
  }, [tracks, item.id, item.type, item.isMainTrack]);

  type VisibleRect = {
    width: number;
    height: number;
    left: number;
    top: number;
  };

  const getVisibleBox = useCallback(
    (target: EditorStarterItem): VisibleRect => {
      const rect =
        target.type === 'group'
          ? resolveGroupGeometry(target, allItems)
          : getRectAfterCrop(target);
      return {
        width: Math.round(rect.width),
        height: Math.round(rect.height),
        left: Math.round(rect.left),
        top: Math.round(rect.top),
      };
    },
    [allItems],
  );

  const applyVisibleRectToItem = useCallback(
    (target: EditorStarterItem, rect: VisibleRect): EditorStarterItem => {
      const crop = getCropFromItem(target);
      const cropLeft = crop?.cropLeft ?? 0;
      const cropTop = crop?.cropTop ?? 0;
      const cropRight = crop?.cropRight ?? 0;
      const cropBottom = crop?.cropBottom ?? 0;
      const cropW = 1 - cropLeft - cropRight;
      const cropH = 1 - cropTop - cropBottom;

      const width = Math.max(1, Math.round(rect.width / cropW));
      const height = Math.max(1, Math.round(rect.height / cropH));
      const left = Math.round(rect.left - width * cropLeft);
      const top = Math.round(rect.top - height * cropTop);

      return {
        ...target,
        width,
        height,
        left,
        top,
      } as EditorStarterItem;
    },
    [],
  );

  const visibleBox = useMemo(() => getVisibleBox(item), [getVisibleBox, item]);

  // 애니가 적용된 현재 프레임의 표시 변형 — outline·핸들이 움직인(이동·확대) 콘텐츠를
  // 따라가게 한다. **표시·히트테스트 전용**이다: 드래그·리사이즈·스냅·정렬 등 편집 수학은
  // 전부 저장 좌표(getVisibleBox) 기준 그대로라 커밋 시 애니 오프셋이 이중 반영되지 않는다.
  const animationDisplay = useMemo(() => {
    const animations =
      'animations' in item &&
      Array.isArray(item.animations) &&
      item.animations.length > 0
        ? (item.animations as ItemAnimation[])
        : null;
    const positionKeyframes =
      'positionKeyframes' in item &&
      Array.isArray(item.positionKeyframes) &&
      item.positionKeyframes.length > 0
        ? item.positionKeyframes
        : undefined;
    if (!animations && !positionKeyframes) return null;

    // 이 컴포넌트는 아이템 자신의 Sequence 안이라 currentFrame 은 로컬 프레임 —
    // 렌더러(inner-layer/GroupItemLayer)와 동일 규약이다.
    const totalDurationInFrames =
      item.type === 'group'
        ? Math.max(
            1,
            resolveGroupGeometry(item, allItems).durationInFrames,
          )
        : Math.max(1, item.durationInFrames);
    const values = calculateAnimationsValues({
      animations: animations ?? [],
      currentFrame,
      framesPerSecond: fps,
      totalDurationInFrames,
      positionKeyframes,
    });

    const offsetX =
      (values.translateX / 100) * visibleBox.width + values.translateXPx;
    const offsetY =
      (values.translateY / 100) * visibleBox.height + values.translateYPx;
    if (offsetX === 0 && offsetY === 0 && values.scale === 1) return null;
    return { offsetX, offsetY, scale: values.scale };
  }, [item, allItems, currentFrame, fps, visibleBox.width, visibleBox.height]);
  const animOffsetX = animationDisplay?.offsetX ?? 0;
  const animOffsetY = animationDisplay?.offsetY ?? 0;
  const animScale = animationDisplay?.scale ?? 1;

  // 키프레임 드래그 중 표시 앵커 — 드래그는 base(left/top)를 실시간으로 움직였다가
  // 놓을 때 원복 + delta 를 키프레임에 기록한다. base 기준으로 그리는 표시물(모션 패스·
  // 원위치 고스트)이 드래그 동안 아이템을 따라 밀리므로, 드래그 시작 시점 박스를 박제해
  // 고정한다. 표시 전용.
  const [keyframeDragAnchor, setKeyframeDragAnchor] = React.useState<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);

  // 모션 패스 — 위치 키프레임 경로(아이템 중심 기준)를 점선 + 점으로 보여준다. 표시 전용.
  const motionPath = useMemo(() => {
    const keyframes =
      'positionKeyframes' in item && Array.isArray(item.positionKeyframes)
        ? item.positionKeyframes
        : [];
    if (keyframes.length < 2) return null;
    const box = keyframeDragAnchor ?? visibleBox;
    const cx = box.left + box.width / 2;
    const cy = box.top + box.height / 2;
    return keyframes.map((k) => ({ x: cx + k.dx, y: cy + k.dy }));
  }, [item, keyframeDragAnchor, visibleBox]);

  // 원위치 고스트 — 애니로 이동해 있는 동안 저장 위치를 점선으로 함께 보여준다.
  // "위치가 시간 따라 바뀌는" 불안감 해소용 표시 전용(포인터 무시). 선택 시에만.
  // 키프레임 드래그 중에는 모션 패스와 같은 이유로 드래그 시작 박스에 고정한다.
  const ghostStyle: React.CSSProperties | null = useMemo(() => {
    if (animOffsetX === 0 && animOffsetY === 0) return null;
    const box = keyframeDragAnchor ?? visibleBox;
    return {
      position: 'absolute',
      width: box.width,
      height: box.height,
      left: box.left,
      top: box.top,
      outline: `${scaledBorder}px dashed var(--color-editor-starter-accent)`,
      opacity: 0.4,
      transform: 'rotation' in item ? `rotate(${item.rotation}deg)` : undefined,
      pointerEvents: 'none',
      userSelect: 'none',
      zIndex: 999,
    };
  }, [
    animOffsetX,
    animOffsetY,
    item,
    scaledBorder,
    keyframeDragAnchor,
    visibleBox,
  ]);

  const [hovered, setHovered] = React.useState(false);
  const [focusedCodeFieldKey, setFocusedCodeFieldKey] = React.useState<
    string | null
  >(null);
  const [draftCodeFieldGeometries, setDraftCodeFieldGeometries] =
    React.useState<Record<string, ItemGeometry> | null>(null);
  const [editingCodeField, setEditingCodeField] = React.useState<{
    fieldKey: string;
    initialValue: string;
  } | null>(null);

  const onMouseEnter = useCallback(() => {
    setHovered(true);
    if (item.groupId != null) onHoverGroupChange?.(item.groupId);
  }, [item.groupId, onHoverGroupChange]);

  const onMouseLeave = useCallback(() => {
    setHovered(false);
    if (item.groupId != null) onHoverGroupChange?.(null);
  }, [item.groupId, onHoverGroupChange]);

  const selected = selectedItems.includes(item.id);
  // 그룹을 한 덩어리로 선택한 상태(같은 그룹 멤버 2개 이상 전체 선택)에서는 개별 멤버
  // outline 을 숨기고 GroupSelectionOutline(union bbox)만 보이게 한다. depth 진입(멤버 1개)
  // 일 때는 개별 outline 을 유지해 어떤 멤버를 편집 중인지 보이게 한다.
  const isGroupBlockSelected =
    activeGroupId != null &&
    item.groupId === activeGroupId &&
    selectedItems.length > 1;
  // 같은 그룹의 멤버에 hover 중이면 개별 hover outline 대신 그룹 union outline
  // (SortedOutlines 가 렌더)을 보여준다.
  const isInHoveredGroup =
    hoveredGroupId != null && item.groupId === hoveredGroupId;
  const codeEditableFields = useMemo(() => {
    if (item.type !== 'code' || !selected) {
      return [];
    }

    return getActiveCodeEditableFields({
      code: item.code,
      compositionWidth,
      compositionHeight,
      frame: currentFrame,
      fps,
    });
  }, [compositionHeight, compositionWidth, currentFrame, fps, item, selected]);
  const codeActiveFieldKeys = useMemo(
    () => getCodeActiveFieldKeys(item),
    [item],
  );
  const codeActiveFieldKeySet = useMemo(
    () => new Set(codeActiveFieldKeys),
    [codeActiveFieldKeys],
  );

  const selectCodeFields = useCallback(
    (fieldKeys: string[]) => {
      if (isLocked) {
        return;
      }

      const nextFieldKeys = normalizeCodeFieldSelection(fieldKeys);
      const primaryFieldKey = getPrimaryCodeFieldKey(nextFieldKeys);

      setState({
        update: (state) =>
          setActiveSelectionSurface(
            setSelectedItems(
              changeItem(state, item.id, (currentItem) =>
                currentItem.type === 'code'
                  ? {
                      ...currentItem,
                      codeActiveField: primaryFieldKey,
                      codeActiveFields: nextFieldKeys,
                      shinhanPdfActiveField: null,
                    }
                  : currentItem,
              ),
              [item.id],
            ),
            'canvas',
          ),
        commitToUndoStack: false,
      });
    },
    [isLocked, item.id, setState],
  );

  const startCodeFieldInlineEdit = useCallback(
    (field: CodeEditableField, event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (isLocked || !field.geometry) {
        return;
      }

      setEditingCodeField({
        fieldKey: field.key,
        initialValue: field.value,
      });
      setState({
        update: (state) =>
          setActiveSelectionSurface(
            setSelectedItems(
              changeItem(state, item.id, (currentItem) =>
                currentItem.type === 'code'
                  ? {
                      ...currentItem,
                      codeActiveField: field.key,
                      codeActiveFields: [field.key],
                      shinhanPdfActiveField: null,
                    }
                  : currentItem,
              ),
              [item.id],
            ),
            'canvas',
          ),
        commitToUndoStack: false,
      });
    },
    [isLocked, item.id, setState],
  );

  const commitCodeFieldText = useCallback(
    ({
      fieldKey,
      initialValue,
      nextValue,
    }: {
      fieldKey: string;
      initialValue: string;
      nextValue: string;
    }) => {
      setEditingCodeField(null);

      if (nextValue === initialValue) {
        return;
      }

      setState({
        update: (state) =>
          setActiveSelectionSurface(
            setSelectedItems(
              changeItem(state, item.id, (currentItem) => {
                if (currentItem.type !== 'code') {
                  return currentItem;
                }

                const nextCode = updateCodeEditableField(
                  currentItem.code,
                  fieldKey,
                  nextValue,
                );

                return {
                  ...currentItem,
                  code: nextCode ?? currentItem.code,
                  compiledError: nextCode
                    ? undefined
                    : currentItem.compiledError,
                  codeActiveField: fieldKey,
                  codeActiveFields: [fieldKey],
                  shinhanPdfActiveField: null,
                };
              }),
              [item.id],
            ),
            'canvas',
          ),
        commitToUndoStack: true,
      });
    },
    [item.id, setState],
  );

  const commitCodeFieldGeometry = useCallback(
    ({
      fieldKey,
      initialGeometry,
      nextGeometry,
    }: {
      fieldKey: string;
      initialGeometry: ItemGeometry;
      nextGeometry: ItemGeometry;
    }) => {
      const committedGeometry = clampCodeFieldGeometry(
        nextGeometry,
        compositionWidth,
        compositionHeight,
      );
      const didChange = geometryChanged(initialGeometry, committedGeometry);
      const currentItem = stateAsRef.current.undoableState.items[item.id];
      const nextCode =
        didChange && currentItem?.type === 'code'
          ? updateCodeEditableFieldGeometry({
              code: currentItem.code,
              fieldKey,
              geometry: committedGeometry,
              compositionWidth,
              compositionHeight,
            })
          : null;

      if (didChange && !nextCode) {
        return;
      }

      setState({
        update: (state) =>
          setActiveSelectionSurface(
            setSelectedItems(
              changeItem(state, item.id, (currentItem) => {
                if (currentItem.type !== 'code') {
                  return currentItem;
                }

                return {
                  ...currentItem,
                  code: nextCode ?? currentItem.code,
                  compiledError: nextCode
                    ? undefined
                    : currentItem.compiledError,
                  codeActiveField: fieldKey,
                  codeActiveFields: [fieldKey],
                  shinhanPdfActiveField: null,
                };
              }),
              [item.id],
            ),
            'canvas',
          ),
        commitToUndoStack: didChange,
      });
    },
    [compositionHeight, compositionWidth, item.id, setState, stateAsRef],
  );

  const commitCodeFieldGeometries = useCallback(
    ({
      initialGeometries,
      nextGeometries,
      primaryFieldKey,
      selectedFieldKeys,
    }: {
      initialGeometries: Record<string, ItemGeometry>;
      nextGeometries: Record<string, ItemGeometry>;
      primaryFieldKey: string;
      selectedFieldKeys: string[];
    }) => {
      const committedGeometries = Object.fromEntries(
        Object.entries(nextGeometries).map(([fieldKey, geometry]) => [
          fieldKey,
          clampCodeFieldGeometry(geometry, compositionWidth, compositionHeight),
        ]),
      );
      const didChange = Object.entries(committedGeometries).some(
        ([fieldKey, geometry]) => {
          const initialGeometry = initialGeometries[fieldKey];
          return initialGeometry
            ? geometryChanged(initialGeometry, geometry)
            : false;
        },
      );
      const currentItem = stateAsRef.current.undoableState.items[item.id];
      let nextCode = currentItem?.type === 'code' ? currentItem.code : null;
      let didUpdateCode = false;

      if (didChange) {
        if (!nextCode) {
          return;
        }

        for (const [fieldKey, geometry] of Object.entries(
          committedGeometries,
        )) {
          const updatedCode = updateCodeEditableFieldGeometry({
            code: nextCode,
            fieldKey,
            geometry,
            compositionWidth,
            compositionHeight,
          });

          if (!updatedCode) {
            return;
          }

          nextCode = updatedCode;
          didUpdateCode = true;
        }
      }

      setState({
        update: (state) =>
          setActiveSelectionSurface(
            setSelectedItems(
              changeItem(state, item.id, (currentItem) => {
                if (currentItem.type !== 'code') {
                  return currentItem;
                }

                return {
                  ...currentItem,
                  code: nextCode ?? currentItem.code,
                  compiledError: didUpdateCode
                    ? undefined
                    : currentItem.compiledError,
                  codeActiveField: primaryFieldKey,
                  codeActiveFields: selectedFieldKeys,
                  shinhanPdfActiveField: null,
                };
              }),
              [item.id],
            ),
            'canvas',
          ),
        commitToUndoStack: didChange,
      });
    },
    [compositionHeight, compositionWidth, item.id, setState, stateAsRef],
  );

  const startCodeFieldMove = useCallback(
    (
      fieldKey: string,
      geometry: ItemGeometry,
      event: React.PointerEvent<HTMLButtonElement>,
    ) => {
      event.stopPropagation();

      if (isLocked || !isLeftClick(event)) {
        return;
      }

      const multiSelect = event.metaKey || event.shiftKey;
      const targetWasActive = codeActiveFieldKeySet.has(fieldKey);
      const nextSelectedFieldKeys = getNextCodeFieldSelection({
        currentFieldKeys: codeActiveFieldKeys,
        fieldKey,
        multiSelect,
      });
      const selectedFieldKeysForMove =
        targetWasActive && !multiSelect
          ? codeActiveFieldKeys
          : nextSelectedFieldKeys;
      const selectedFieldKeysForState =
        targetWasActive && !multiSelect && codeActiveFieldKeys.length > 1
          ? codeActiveFieldKeys
          : nextSelectedFieldKeys;
      const primaryFieldKey =
        getPrimaryCodeFieldKey(selectedFieldKeysForState) ?? fieldKey;

      selectCodeFields(selectedFieldKeysForState);

      if (multiSelect && targetWasActive && codeActiveFieldKeys.length > 1) {
        return;
      }

      const initialX = event.clientX;
      const initialY = event.clientY;
      const selectedFieldGeometryEntries = codeEditableFields
        .filter((field) => selectedFieldKeysForMove.includes(field.key))
        .flatMap((field) =>
          field.geometry
            ? [
                [
                  field.key,
                  clampCodeFieldGeometry(
                    field.key === fieldKey ? geometry : field.geometry,
                    compositionWidth,
                    compositionHeight,
                  ),
                ] as const,
              ]
            : [],
        );
      const initialGeometries =
        selectedFieldGeometryEntries.length > 0
          ? Object.fromEntries(selectedFieldGeometryEntries)
          : {
              [fieldKey]: clampCodeFieldGeometry(
                geometry,
                compositionWidth,
                compositionHeight,
              ),
            };
      let latestGeometries = initialGeometries;

      setDraftCodeFieldGeometries(initialGeometries);

      const onPointerMove = (pointerMoveEvent: PointerEvent) => {
        const offsetX = (pointerMoveEvent.clientX - initialX) / scale;
        const offsetY = (pointerMoveEvent.clientY - initialY) / scale;
        latestGeometries = Object.fromEntries(
          Object.entries(initialGeometries).map(
            ([currentFieldKey, initial]) => [
              currentFieldKey,
              clampCodeFieldGeometry(
                {
                  ...initial,
                  left: initial.left + offsetX,
                  top: initial.top + offsetY,
                },
                compositionWidth,
                compositionHeight,
              ),
            ],
          ),
        );
        setDraftCodeFieldGeometries(latestGeometries);
      };

      const onPointerUp = () => {
        window.removeEventListener('pointermove', onPointerMove);
        setDraftCodeFieldGeometries(null);
        commitCodeFieldGeometries({
          initialGeometries,
          nextGeometries: latestGeometries,
          primaryFieldKey,
          selectedFieldKeys: selectedFieldKeysForMove,
        });
      };

      window.addEventListener('pointermove', onPointerMove, { passive: true });
      window.addEventListener('pointerup', onPointerUp, { once: true });
    },
    [
      codeActiveFieldKeySet,
      codeActiveFieldKeys,
      codeEditableFields,
      commitCodeFieldGeometries,
      compositionHeight,
      compositionWidth,
      isLocked,
      scale,
      selectCodeFields,
    ],
  );

  const startCodeFieldResize = useCallback(
    (
      fieldKey: string,
      geometry: ItemGeometry,
      type: CodeFieldResizeHandleType,
      event: React.PointerEvent<HTMLDivElement>,
    ) => {
      event.stopPropagation();

      if (isLocked || !isLeftClick(event)) {
        return;
      }

      const initialX = event.clientX;
      const initialY = event.clientY;
      const initialGeometry = clampCodeFieldGeometry(
        geometry,
        compositionWidth,
        compositionHeight,
      );
      let latestGeometry = initialGeometry;

      setDraftCodeFieldGeometries({ [fieldKey]: initialGeometry });

      const onPointerMove = (pointerMoveEvent: PointerEvent) => {
        const offsetX = (pointerMoveEvent.clientX - initialX) / scale;
        const offsetY = (pointerMoveEvent.clientY - initialY) / scale;
        latestGeometry = resizeCodeFieldGeometry({
          type,
          geometry: initialGeometry,
          offsetX,
          offsetY,
          compositionWidth,
          compositionHeight,
        });
        setDraftCodeFieldGeometries({ [fieldKey]: latestGeometry });
      };

      const onPointerUp = () => {
        window.removeEventListener('pointermove', onPointerMove);
        setDraftCodeFieldGeometries(null);
        commitCodeFieldGeometry({
          fieldKey,
          initialGeometry,
          nextGeometry: latestGeometry,
        });
      };

      window.addEventListener('pointermove', onPointerMove, { passive: true });
      window.addEventListener('pointerup', onPointerUp, { once: true });
    },
    [
      commitCodeFieldGeometry,
      compositionHeight,
      compositionWidth,
      isLocked,
      scale,
    ],
  );

  // 선택 UI(리사이즈·크롭 핸들) 노출 조건. outline div 가 아니라 별도 상위 레이어에 렌더한다.
  const showResizeUi =
    selected &&
    !isLocked &&
    !itemIsBeingTextEdited &&
    !itemIsBeingCropped &&
    selectedItems.length === 1 &&
    // 그룹은 리사이즈 핸들을 숨겨 move-only 로 제한한다. children 스케일은 정의가
    // 필요한 별도 기능이라 지금 노출하면 반쪽 동작(핸들만 있고 실제 스케일 미적용)이 된다.
    item.type !== 'group';

  // 핸들 전용 레이어 — outline div(zIndex 1000)의 자식으로 두면 다른 아이템의 히트영역
  // (DOM 뒤쪽, 같은 zIndex)이 핸들 위를 덮을 때 포인터를 가로채 리사이즈가 안 된다.
  // 형제 레이어를 1001 로 올려 핸들만 항상 최상위로. 레이어 자체는 pointerEvents none 이라
  // 아이템 본문 클릭 우선순위(위 트랙 우선)는 기존 그대로다.
  const handleLayerStyle: React.CSSProperties = useMemo(() => {
    const transforms = [
      'rotation' in item ? `rotate(${item.rotation}deg)` : null,
      // 애니 확대는 표시로만 따라간다(리사이즈 커밋은 저장 크기 기준).
      animScale !== 1 ? `scale(${animScale})` : null,
    ].filter(Boolean);
    return {
      position: 'absolute',
      width: visibleBox.width,
      height: visibleBox.height,
      left: visibleBox.left + animOffsetX,
      top: visibleBox.top + animOffsetY,
      transform: transforms.length > 0 ? transforms.join(' ') : undefined,
      pointerEvents: 'none',
      userSelect: 'none',
      zIndex: 1001,
    };
  }, [
    item,
    animOffsetX,
    animOffsetY,
    animScale,
    visibleBox.height,
    visibleBox.left,
    visibleBox.top,
    visibleBox.width,
  ]);

  // 크롭 모드에서 "원본 영상 크기 조정" 핸들을 붙일 레이어 — 크롭 프레임(visibleBox)이 아니라
  // 원본 소스 박스(item.left/top/width/height)에 정렬한다. 크롭 모드에서는 전체 원본이
  // dimmed 로 렌더되므로(FEATURE_CROP_BACKGROUNDS) 소스 꼭짓점에 핸들이 얹힌다.
  const sourceBoxStyle: React.CSSProperties | null = useMemo(() => {
    if (!getCanCrop(item)) return null;
    const transforms = [
      'rotation' in item ? `rotate(${item.rotation}deg)` : null,
      animScale !== 1 ? `scale(${animScale})` : null,
    ].filter(Boolean);
    // 캔버스 스케일에 반비례한 두께로 화면상 일정한 실선을 유지한다.
    const borderWidth = Math.max(1, Math.round(1 / scale));
    return {
      position: 'absolute',
      width: item.width,
      height: item.height,
      left: item.left + animOffsetX,
      top: item.top + animOffsetY,
      transform: transforms.length > 0 ? transforms.join(' ') : undefined,
      // 꼭짓점(마름모)을 잇는 실선 — 원본이 확대돼 꼭짓점이 뷰포트를 벗어나도
      // 이 선이 화면을 가로질러 원본 크기 조정 영역이 있음을 알려준다.
      border: `${borderWidth}px solid var(--color-editor-starter-accent)`,
      pointerEvents: 'none',
      userSelect: 'none',
      zIndex: 1001,
    };
  }, [item, animOffsetX, animOffsetY, animScale, scale]);

  const style: React.CSSProperties = useMemo(() => {
    const color = itemIsBeingCropped
      ? 'gray'
      : 'var(--color-editor-starter-accent)';
    const cursor: React.CSSProperties['cursor'] = isLocked
      ? 'not-allowed'
      : itemIsBeingCropped
        ? 'move'
        : 'default';
    const transforms = [
      'rotation' in item ? `rotate(${item.rotation}deg)` : null,
      // 애니 확대는 표시로만 따라간다(편집 커밋은 저장 크기 기준).
      animScale !== 1 ? `scale(${animScale})` : null,
    ].filter(Boolean);

    return {
      cursor,
      width: visibleBox.width,
      height: visibleBox.height,
      // 애니 이동 오프셋을 더해 outline·히트영역이 움직인 콘텐츠 위치를 따라간다.
      left: visibleBox.left + animOffsetX,
      top: visibleBox.top + animOffsetY,
      position: 'absolute',
      outline:
        (selected || (hovered && !textItemEditing && !isInHoveredGroup)) &&
        !isGroupBlockSelected
          ? `${scaledBorder}px solid ${color}`
          : undefined,
      userSelect: 'none',
      touchAction: 'none',
      transform: transforms.length > 0 ? transforms.join(' ') : undefined,
      pointerEvents:
        item.type === 'text' && item.id === textItemEditing
          ? 'none'
          : editMode === 'draw-solid' ||
              editMode === 'draw-blur' ||
              editMode === 'hand'
            ? 'none'
            : 'auto',
      // 레이어들의 zIndex보다 높게 설정하여 선택 UI가 항상 위에 보이도록 함.
      // 핸들 가림은 outline 을 올리는 대신 별도 핸들 레이어(zIndex 1001)가 해결한다 —
      // 선택 div 자체를 올리면 전체화면 배경 선택 시 다른 아이템 클릭이 막힌다.
      zIndex: 1000,
    };
  }, [
    editMode,
    hovered,
    isLocked,
    item,
    itemIsBeingCropped,
    scaledBorder,
    selected,
    isGroupBlockSelected,
    isInHoveredGroup,
    textItemEditing,
    animOffsetX,
    animOffsetY,
    animScale,
    visibleBox.height,
    visibleBox.left,
    visibleBox.top,
    visibleBox.width,
  ]);

  const startDragging = useCallback(
    (
      e: PointerEvent | React.MouseEvent,
      selectedItemIds: string[],
      onClickWithoutMove?: () => void,
    ) => {
      const initialX = e.clientX;
      const initialY = e.clientY;

      let offsetX = 0;
      let offsetY = 0;

      const items = stateAsRef.current.undoableState.items;

      // 일반 드래그용 (visible area 기준)
      const originalVisibleLeft = selectedItemIds.map((id) => {
        const current = items[id];
        if (!current) {
          return 0;
        }
        return getVisibleBox(current as EditorStarterItem).left;
      });
      const originalVisibleTop = selectedItemIds.map((id) => {
        const current = items[id];
        if (!current) {
          return 0;
        }
        return getVisibleBox(current as EditorStarterItem).top;
      });

      // crop 모드용 (item 기준) - 업스트림 방식
      const originalLeft = selectedItemIds.map((id) => items[id]?.left ?? 0);
      const originalTop = selectedItemIds.map((id) => items[id]?.top ?? 0);
      const originalWidth = selectedItemIds.map((id) => items[id]?.width ?? 1);
      const originalHeight = selectedItemIds.map(
        (id) => items[id]?.height ?? 1,
      );
      const originalCrop = selectedItemIds.map((id) =>
        getCropFromItem(items[id] as EditorStarterItem),
      );

      let didMove = false;
      const multiSelect = e.metaKey || e.shiftKey;
      let shiftPressed = e.shiftKey;

      // 키프레임 드래그(단일 선택·이 아이템)면 표시 앵커를 시작 박스로 고정한다.
      if (selectedItemIds.length === 1 && selectedItemIds[0] === item.id) {
        const dragged = items[item.id];
        if (
          dragged &&
          'positionKeyframes' in dragged &&
          Array.isArray(dragged.positionKeyframes) &&
          dragged.positionKeyframes.length > 0
        ) {
          const box = getVisibleBox(dragged as EditorStarterItem);
          setKeyframeDragAnchor({
            left: box.left,
            top: box.top,
            width: box.width,
            height: box.height,
          });
        }
      }

      // 스냅은 시각 공간에서 — 애니(이동)가 붙은 아이템은 저장 좌표와 보이는 위치가
      // 달라, 저장 좌표로 스냅하면 가이드선과 보이는 박스가 애니 오프셋만큼 어긋난다.
      // 드래그 대상은 자기 오프셋(animOffsetX/Y)을 더해 스냅하고, 커밋 시 도로 뺀다.
      const draggedAnimOffsetX = animOffsetX;
      const draggedAnimOffsetY = animOffsetY;

      // 아이템 간 스냅 target — 드래그 시작 시 1회 수집(드래그 중 다른 아이템은 불변).
      // 숨김 트랙·audio·드래그 대상(그룹이면 children 포함)·드래그 멤버를 포함한 그룹·
      // 현재 프레임에 안 보이는 아이템은 제외한다. target 도 애니 오프셋을 더한
      // 시각 위치로 넣는다.
      const snapTargets: SnapTargetRect[] = (() => {
        if (selectedItemIds.length !== 1) return [];
        const draggedId = selectedItemIds[0] as string;
        const dragged = items[draggedId];
        const excluded = new Set(selectedItemIds);
        if (dragged?.type === 'group') {
          dragged.children.forEach((id) => excluded.add(id));
        }

        // 이 컴포넌트는 SortedOutlines 의 Sequence(from=드래그 아이템의 geometry.from)
        // 안이라 useCurrentFrame 이 로컬 프레임이다. target 의 from/duration 은 글로벌
        // 타임라인 좌표이므로 글로벌 프레임으로 환산해 비교한다 — 안 그러면 from > 0 인
        // 아이템(뒷 씬)을 드래그할 때 지금 보이는 target 이 전부 시간 밖으로 오판된다.
        const draggedFrom =
          dragged?.type === 'group'
            ? resolveGroupGeometry(dragged, items).from
            : (dragged?.from ?? 0);
        const globalFrame = currentFrame + draggedFrom;

        const rects: SnapTargetRect[] = [];
        for (const track of stateAsRef.current.undoableState.tracks) {
          if (track.hidden) continue;
          for (const id of track.items) {
            if (excluded.has(id)) continue;
            const target = items[id];
            if (!target || target.type === 'audio') continue;
            if (
              target.type === 'group' &&
              target.children.includes(draggedId)
            ) {
              continue;
            }
            const geo =
              target.type === 'group'
                ? resolveGroupGeometry(target, items)
                : {
                    ...getRectAfterCrop(target),
                    from: target.from,
                    durationInFrames: target.durationInFrames,
                  };
            if (
              globalFrame < geo.from ||
              globalFrame >= geo.from + geo.durationInFrames
            ) {
              continue;
            }

            // target 의 애니(이동) 오프셋 — 보이는 위치 기준으로 스냅되게 한다.
            let targetAnimOffsetX = 0;
            let targetAnimOffsetY = 0;
            const targetAnimations =
              'animations' in target &&
              Array.isArray(target.animations) &&
              target.animations.length > 0
                ? (target.animations as ItemAnimation[])
                : null;
            if (targetAnimations) {
              const values = calculateAnimationsValues({
                animations: targetAnimations,
                currentFrame: globalFrame - geo.from,
                framesPerSecond: fps,
                totalDurationInFrames: Math.max(1, geo.durationInFrames),
              });
              targetAnimOffsetX =
                (values.translateX / 100) * geo.width + values.translateXPx;
              targetAnimOffsetY =
                (values.translateY / 100) * geo.height + values.translateYPx;
            }

            rects.push({
              left: geo.left + targetAnimOffsetX,
              top: geo.top + targetAnimOffsetY,
              width: geo.width,
              height: geo.height,
            });
          }
        }
        return rects;
      })();

      const reposition = () => {
        let axisLocked: 'horizontal' | 'vertical' | null = null;

        if (FEATURE_SHIFT_AXIS_LOCK && shiftPressed) {
          const totalMovementX = Math.abs(offsetX);
          const totalMovementY = Math.abs(offsetY);

          if (
            totalMovementX > AXIS_LOCK_THRESHOLD ||
            totalMovementY > AXIS_LOCK_THRESHOLD
          ) {
            axisLocked =
              totalMovementX > totalMovementY ? 'horizontal' : 'vertical';
          }
        }

        const isSingleItem = selectedItemIds.length === 1;
        const currentItems = stateAsRef.current.undoableState.items;

        // crop 모드가 아닐 때만 스냅 가이드 적용
        let snapResultForGuides: ReturnType<typeof calculateMoveSnap> | null =
          null;

        if (isSingleItem && smartGuides && !itemIsBeingCropped) {
          const singleItemId = selectedItemIds[0] as string;
          const currentItem = currentItems[singleItemId];
          if (currentItem) {
            const baseLeft =
              axisLocked === 'vertical'
                ? (originalVisibleLeft[0] as number)
                : Math.round((originalVisibleLeft[0] ?? 0) + offsetX);
            const baseTop =
              axisLocked === 'horizontal'
                ? (originalVisibleTop[0] as number)
                : Math.round((originalVisibleTop[0] ?? 0) + offsetY);

            // 그룹은 저장 bbox 가 stale 할 수 있어 getVisibleBox(children union)로 계산
            const currentVisibleRect = getVisibleBox(
              currentItem as EditorStarterItem,
            );
            const currentVisibleWidth = currentVisibleRect.width;
            const currentVisibleHeight = currentVisibleRect.height;

            // 시각 공간(저장 좌표 + 애니 오프셋)에서 스냅 — 가이드선·붙는 지점이
            // 화면에 보이는 모서리와 일치한다. 커밋 시 오프셋을 도로 빼 저장 좌표로.
            snapResultForGuides = calculateMoveSnap({
              itemLeft: baseLeft + draggedAnimOffsetX,
              itemTop: baseTop + draggedAnimOffsetY,
              itemWidth: currentVisibleWidth,
              itemHeight: currentVisibleHeight,
              canvasWidth: compositionWidth,
              canvasHeight: compositionHeight,
              targets: snapTargets,
            });

            smartGuides.setActiveGuides(snapResultForGuides.activeGuides);
          }
        }

        for (let idx = 0; idx < selectedItemIds.length; idx++) {
          const itemId = selectedItemIds[idx] as string;

          setState({
            groupPropertyEdit: 'position',
            update: (state) => {
              return changeItem(state, itemId, (i) => {
                // crop 모드: 업스트림 방식 - item.left/top 이동 + crop 값 조정
                if (itemIsBeingCropped) {
                  const newLeft =
                    axisLocked === 'vertical'
                      ? (originalLeft[idx] as number)
                      : Math.round((originalLeft[idx] ?? 0) + offsetX);
                  const newTop =
                    axisLocked === 'horizontal'
                      ? (originalTop[idx] as number)
                      : Math.round((originalTop[idx] ?? 0) + offsetY);

                  const updatedItem: EditorStarterItem = {
                    ...(i as EditorStarterItem),
                    left: newLeft,
                    top: newTop,
                  };

                  const shiftedX = updatedItem.left - (originalLeft[idx] ?? 0);
                  const shiftedY = updatedItem.top - (originalTop[idx] ?? 0);

                  if (getCanCrop(updatedItem) && originalCrop[idx]) {
                    updatedItem.cropLeft =
                      (originalCrop[idx]?.cropLeft ?? 0) -
                      shiftedX / (originalWidth[idx] ?? 1);
                    updatedItem.cropTop =
                      (originalCrop[idx]?.cropTop ?? 0) -
                      shiftedY / (originalHeight[idx] ?? 1);
                    updatedItem.cropRight =
                      (originalCrop[idx]?.cropRight ?? 0) +
                      shiftedX / (originalWidth[idx] ?? 1);
                    updatedItem.cropBottom =
                      (originalCrop[idx]?.cropBottom ?? 0) +
                      shiftedY / (originalHeight[idx] ?? 1);
                  }

                  return updatedItem;
                }

                // 일반 모드: 기존 방식 - visible area 이동
                let newVisibleLeft =
                  axisLocked === 'vertical'
                    ? (originalVisibleLeft[idx] as number)
                    : Math.round((originalVisibleLeft[idx] ?? 0) + offsetX);
                let newVisibleTop =
                  axisLocked === 'horizontal'
                    ? (originalVisibleTop[idx] as number)
                    : Math.round((originalVisibleTop[idx] ?? 0) + offsetY);

                if (isSingleItem && snapResultForGuides) {
                  // 스냅은 시각 공간에서 계산됐으므로 애니 오프셋을 빼 저장 좌표로 환산.
                  newVisibleLeft =
                    snapResultForGuides.snappedLeft - draggedAnimOffsetX;
                  newVisibleTop =
                    snapResultForGuides.snappedTop - draggedAnimOffsetY;
                }

                // A group's stored box may differ from its children union.
                // Translate the captured origin by the visible drag delta;
                // replacing it with the union box would jump/rescale children.
                if (i.type === 'group') {
                  return {
                    ...i,
                    left: (originalLeft[idx] ?? i.left) + newVisibleLeft - (originalVisibleLeft[idx] ?? 0),
                    top: (originalTop[idx] ?? i.top) + newVisibleTop - (originalVisibleTop[idx] ?? 0),
                  };
                }
                const currentVisible = getVisibleBox(i as EditorStarterItem);
                const updatedItem = applyVisibleRectToItem(
                  i as EditorStarterItem,
                  {
                    left: newVisibleLeft,
                    top: newVisibleTop,
                    width: currentVisible.width,
                    height: currentVisible.height,
                  },
                );
                return updatedItem as EditorStarterItem;
              });
            },
            commitToUndoStack: false,
          });
        }
      };

      const onPointerMove = (pointerMoveEvent: PointerEvent) => {
        offsetX = (pointerMoveEvent.clientX - initialX) / scale;
        offsetY = (pointerMoveEvent.clientY - initialY) / scale;

        shiftPressed = pointerMoveEvent.shiftKey;
        didMove = true;
        reposition();
      };

      const onPointerUp = () => {
        smartGuides?.clearGuides();

        // 이동이 없는 클릭(예: 이미 선택된 1급 그룹 안 멤버 선택)은 caller 가 넘긴
        // 처리로 대체한다. 드래그(didMove)면 아래 기본 선택 확정 로직을 그대로 탄다.
        if (onClickWithoutMove && !didMove && !multiSelect) {
          onClickWithoutMove();
          cleanup();
          return;
        }

        setState({
          groupPropertyEdit: 'position',
          update: (state) => {
            // 위치 키프레임 아이템의 드래그 = "현재 프레임 키프레임 기록"(CapCut 방식).
            // 드래그 중에는 기존대로 기준 위치(left/top)가 움직였으므로, 여기서 기준을
            // 원복하고 그 delta 를 현재 프레임 키프레임에 넣는다 — 놓는 순간의 화면
            // 위치는 그대로라 사용자 눈에는 이어져 보인다. undo 1회로 함께 묶인다.
            if (didMove && selectedItemIds.length === 1) {
              const draggedId = selectedItemIds[0] as string;
              const current = state.undoableState.items[draggedId];
              const keyframes =
                current &&
                'positionKeyframes' in current &&
                Array.isArray(current.positionKeyframes)
                  ? current.positionKeyframes
                  : [];
              if (current && keyframes.length > 0) {
                const deltaX = current.left - (originalLeft[0] ?? current.left);
                const deltaY = current.top - (originalTop[0] ?? current.top);
                if (deltaX !== 0 || deltaY !== 0) {
                  const frame = Math.max(0, Math.round(currentFrame));
                  const base = getPositionOffsetAt(keyframes, frame);
                  const nextKeyframes = upsertPositionKeyframe(keyframes, {
                    frame,
                    dx: base.dx + deltaX,
                    dy: base.dy + deltaY,
                  });
                  state = changeItem(state, draggedId, (i) => {
                    return {
                      ...(i as EditorStarterItem),
                      left: originalLeft[0] ?? i.left,
                      top: originalTop[0] ?? i.top,
                      positionKeyframes: nextKeyframes,
                    } as EditorStarterItem;
                  });
                }
              }
            }

            // 그룹 선택/depth 상태(activeGroupId)면 onPointerDown 에서 확정한 선택을
            // 그대로 유지한다. setSelectedItems 로 덮으면 activeGroupId 가 리셋되어
            // 클릭 후 개별로 풀리거나 드래그 후 그룹이 일반 multi-select 로 떨어진다.
            if (state.activeGroupId != null) {
              return state;
            }
            return setSelectedItems(
              state,
              !didMove && !multiSelect ? [item.id] : state.selectedItems,
            );
          },
          commitToUndoStack: true,
        });

        cleanup();
      };

      const onKeyDown = (evt: KeyboardEvent) => {
        if (evt.key === 'Shift') {
          shiftPressed = true;
          reposition();
        }
      };

      const onKeyUp = (evt: KeyboardEvent) => {
        if (evt.key === 'Shift') {
          shiftPressed = false;
          reposition();
        }
      };

      const onWindowBlur = () => {
        smartGuides?.clearGuides();
        cleanup();
      };

      const cleanup = () => {
        setKeyframeDragAnchor(null);
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('keydown', onKeyDown);
        window.removeEventListener('keyup', onKeyUp);
        window.removeEventListener('pointerup', onPointerUp);
        window.removeEventListener('blur', onWindowBlur);
      };

      window.addEventListener('pointermove', onPointerMove, { passive: true });
      window.addEventListener('keydown', onKeyDown, { passive: true });
      window.addEventListener('keyup', onKeyUp, { passive: true });
      window.addEventListener('pointerup', onPointerUp, { once: true });
      window.addEventListener('blur', onWindowBlur, { once: true });
    },
    [
      item,
      scale,
      setState,
      stateAsRef,
      smartGuides,
      compositionWidth,
      compositionHeight,
      currentFrame,
      fps,
      animOffsetX,
      animOffsetY,
      getVisibleBox,
      applyVisibleRectToItem,
      itemIsBeingCropped,
    ],
  );

  const onPointerDown = useCallback(
    (e: React.MouseEvent) => {
      if (!isLeftClick(e)) {
        return;
      }
      e.stopPropagation();

      if (
        editMode === 'draw-blur' ||
        editMode === 'draw-solid' ||
        editMode === 'hand'
      ) {
        return;
      }

      // text가 편집 중일 때 drag를 허용하지 않음
      if (item.type === 'text' && item.id === textItemEditing) {
        return;
      }

      // lock된 아이템은 드래그 불가
      if (isLocked) {
        return;
      }

      const multiSelect = e.metaKey || e.shiftKey;

      // 다중 선택 상태에서 선택에 포함된 아이템을 (수정키 없이) 드래그하면 선택 전체가
      // 함께 움직인다. 아래 그룹/그룹멤버 분기보다 먼저 — 안 그러면 클릭한 아이템이
      // 그룹(또는 구세대 그룹 멤버)일 때 그 그룹만 움직이거나 선택이 축소된다.
      // 구세대 그룹 블록 선택(activeGroupId)은 기존 그룹 시맨틱을 유지한다.
      if (
        !multiSelect &&
        activeGroupId == null &&
        selectedItems.length > 1 &&
        selectedItems.includes(item.id)
      ) {
        startDragging(e, selectedItems);
        return;
      }

      // 그룹 멤버 클릭(단일 선택) — 그룹을 한 덩어리처럼 다룬다.
      // - 그룹 미진입(또는 그룹 전체 선택 상태): 같은 그룹 멤버 전체 선택(union bbox).
      // - 그룹에 depth 진입한 상태(selectedItems 1개): 클릭한 멤버만 선택(depth 유지).
      // depth 진입(전체 → 멤버)은 onDoubleClick 이 담당한다.
      const clickedGroupId = item.groupId;
      if (clickedGroupId != null && !multiSelect) {
        const inDepthOfThisGroup =
          activeGroupId === clickedGroupId && selectedItems.length === 1;
        if (inDepthOfThisGroup) {
          setState({
            update: (state) =>
              setActiveSelectionSurface(
                enterGroupMember(state, clickedGroupId, item.id),
                'canvas',
              ),
            commitToUndoStack: true,
          });
          startDragging(e, [item.id]);
        } else {
          setState({
            update: (state) =>
              setActiveSelectionSurface(
                selectGroup(state, clickedGroupId),
                'canvas',
              ),
            commitToUndoStack: true,
          });
          // 드래그 대상 멤버 ids 는 렌더 시점 최신 state(stateAsRef.current)에서 추출한다.
          // setState updater 의 state 와 개념상 다른 시점일 수 있으나, 그룹 선택은 클릭
          // 시점에 즉시 확정되므로 동일 멤버 집합이다(기존 비그룹 드래그도 stateAsRef 사용).
          startDragging(
            e,
            collectGroupMemberIds(stateAsRef.current, clickedGroupId),
          );
        }
        return;
      }

      // 1급 GroupItem 이 이미 선택된 상태의 클릭(드래그 아님)은 하위 멤버를 선택한다.
      // 미선택 상태의 첫 클릭·드래그는 아래 일반 경로가 그룹 전체를 선택/이동한다(변경 없음).
      if (item.type === 'group' && selected && !multiSelect) {
        const group = item;
        const rect = e.currentTarget.getBoundingClientRect();
        // 클릭 좌표 → 컴포지션 절대좌표 (drag 의 scale 처리와 동일한 방식).
        const point = {
          x: visibleBox.left + (e.clientX - rect.left) / scale,
          y: visibleBox.top + (e.clientY - rect.top) / scale,
        };
        // 드래그면 그룹 전체 이동(기본 로직), 이동 없는 클릭이면 멤버 히트테스트.
        startDragging(e, [group.id], () => {
          const memberId = hitTestGroupMember(
            group,
            stateAsRef.current.undoableState.items,
            point.x,
            point.y,
          );
          if (memberId == null) return; // 빈 영역 → 아무것도 안 함
          setState({
            update: (state) => enterGroupMember(state, group.id, memberId),
            commitToUndoStack: false,
          });
        });
        return;
      }

      const updatedSelectedItems = (
        prev: string[],
      ): { allowDrag: boolean; newSelectedItems: string[] } => {
        const isSelected = prev.includes(item.id);
        // shift와 cmd를 누르고 이미 선택된 경우, 해당 item을 선택 해제
        if (isSelected && multiSelect) {
          return {
            allowDrag: false,
            newSelectedItems: prev.filter((id) => id !== item.id),
          };
        }

        // 이미 선택됨, drag 허용
        if (isSelected) {
          return { allowDrag: true, newSelectedItems: prev };
        }

        if (multiSelect) {
          return { allowDrag: true, newSelectedItems: [...prev, item.id] };
        }

        return { allowDrag: true, newSelectedItems: [item.id] };
      };

      setState({
        update: (state) =>
          setActiveSelectionSurface(
            setSelectedItems(
              state,
              updatedSelectedItems(state.selectedItems).newSelectedItems,
            ),
            'canvas',
          ),
        commitToUndoStack: true,
      });

      const { newSelectedItems, allowDrag } =
        updatedSelectedItems(selectedItems);

      // item이 선택 해제되지 않은 경우에만 drag 허용
      if (allowDrag) {
        startDragging(e, newSelectedItems);
      }
    },
    [
      item,
      isLocked,
      selected,
      selectedItems,
      setState,
      startDragging,
      textItemEditing,
      editMode,
      activeGroupId,
      stateAsRef,
      scale,
      visibleBox,
    ],
  );

  const onDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      if (isLocked) {
        return;
      }

      // 1급 GroupItem 더블클릭: 히트된 멤버를 depth 선택하고, text 멤버면 인라인 편집 진입.
      // 그룹 children 은 GroupItemLayer(상대좌표 컨테이너) 안에서 렌더되지만 편집기
      // 위치는 상대 left/top 로 맞춰지므로 절대 위치에 정확히 뜬다(구세대 text 편집과 동일).
      if (item.type === 'group') {
        const group = item;
        const rect = e.currentTarget.getBoundingClientRect();
        const point = {
          x: visibleBox.left + (e.clientX - rect.left) / scale,
          y: visibleBox.top + (e.clientY - rect.top) / scale,
        };
        const currentItems = stateAsRef.current.undoableState.items;
        const memberId = hitTestGroupMember(
          group,
          currentItems,
          point.x,
          point.y,
        );
        if (memberId == null) return; // 빈 영역 → 아무것도 안 함
        e.stopPropagation();
        const member = currentItems[memberId];
        setState({
          update: (state) => {
            const withMember = enterGroupMember(state, group.id, memberId);
            // text 멤버만 인라인 편집 진입. 비-text 는 pointerdown 클릭이 이미 선택.
            return member?.type === 'text'
              ? markTextAsEditing({ state: withMember, itemId: memberId })
              : withMember;
          },
          commitToUndoStack: member?.type === 'text',
        });
        return;
      }

      // 구세대 그룹(groupId): 전체/미진입 상태에서 멤버를 더블클릭하면 그 멤버로 depth 진입.
      // 이미 depth 진입한 상태(selectedItems 1개)면 통과시켜 text 편집/crop 을 유지.
      const dblGroupId = item.groupId;
      if (
        dblGroupId != null &&
        !(activeGroupId === dblGroupId && selectedItems.length === 1)
      ) {
        setState({
          update: (state) =>
            setActiveSelectionSurface(
              enterGroupMember(state, dblGroupId, item.id),
              'canvas',
            ),
          commitToUndoStack: true,
        });
        e.stopPropagation();
        return;
      }

      if (item.type === 'text' && selected) {
        setState({
          update: (state) => {
            return markTextAsEditing({ state, itemId: item.id });
          },
          commitToUndoStack: true,
        });
        e.stopPropagation();
      }

      if (
        FEATURE_CROPPING &&
        FEATURE_DOUBLE_CLICK_TO_CROP &&
        getCanCrop(item) &&
        selected
      ) {
        setState({
          update: (state) => {
            return selectItemForCrop({ state, itemId: item.id });
          },
          commitToUndoStack: true,
        });
        e.stopPropagation();
      }
    },
    [
      isLocked,
      setState,
      item,
      selected,
      activeGroupId,
      selectedItems,
      scale,
      visibleBox,
      stateAsRef,
    ],
  );

  const asset = useAssetIfApplicable(item);
  const currentAssetStatus = asset ? assetStatus[asset.id] : null;

  const uploadProgress = isAssetUploading(currentAssetStatus)
    ? currentAssetStatus.progress
    : null;

  const uploadError = useMemo(() => {
    if (isAssetError(currentAssetStatus)) {
      return currentAssetStatus.error;
    }
    return null;
  }, [currentAssetStatus]);

  const canRetry = canAssetRetryUpload(currentAssetStatus);

  const handleRetry = useCallback(() => {
    if (asset) {
      retryAssetUpload({ asset, setState });
    }
  }, [asset, setState]);

  const handleLayerContent =
    showResizeUi || itemIsBeingCropped ? (
      <>
        <div style={handleLayerStyle}>
          {showResizeUi ? (
            <>
              <ResizeHandle
                height={visibleBox.height}
                width={visibleBox.width}
                left={visibleBox.left}
                top={visibleBox.top}
                itemId={item.id}
                type="top-left"
              />
              <ResizeHandle
                height={visibleBox.height}
                width={visibleBox.width}
                left={visibleBox.left}
                top={visibleBox.top}
                itemId={item.id}
                type="top-right"
              />
              <ResizeHandle
                height={visibleBox.height}
                width={visibleBox.width}
                left={visibleBox.left}
                top={visibleBox.top}
                itemId={item.id}
                type="bottom-left"
              />
              <ResizeHandle
                height={visibleBox.height}
                width={visibleBox.width}
                left={visibleBox.left}
                top={visibleBox.top}
                itemId={item.id}
                type="bottom-right"
              />
              <ResizeHandle
                height={visibleBox.height}
                width={visibleBox.width}
                left={visibleBox.left}
                top={visibleBox.top}
                itemId={item.id}
                type="top"
              />
              <ResizeHandle
                height={visibleBox.height}
                width={visibleBox.width}
                left={visibleBox.left}
                top={visibleBox.top}
                itemId={item.id}
                type="right"
              />
              <ResizeHandle
                height={visibleBox.height}
                width={visibleBox.width}
                left={visibleBox.left}
                top={visibleBox.top}
                itemId={item.id}
                type="bottom"
              />
              <ResizeHandle
                height={visibleBox.height}
                width={visibleBox.width}
                left={visibleBox.left}
                top={visibleBox.top}
                itemId={item.id}
                type="left"
              />
              <SelectionDimensions itemId={item.id} />
              {getCanCrop(item) && <CropButton itemId={item.id} />}
            </>
          ) : null}
          {itemIsBeingCropped ? (
            <>
              <CropHandle
                itemId={item.id}
                type="top-left"
              />
              <CropHandle
                itemId={item.id}
                type="top-right"
              />
              <CropHandle
                itemId={item.id}
                type="bottom-left"
              />
              <CropHandle
                itemId={item.id}
                type="bottom-right"
              />
              <CropHandle
                itemId={item.id}
                type="top"
              />
              <CropHandle
                itemId={item.id}
                type="right"
              />
              <CropHandle
                itemId={item.id}
                type="bottom"
              />
              <CropHandle
                itemId={item.id}
                type="left"
              />
              <SelectionDimensions itemId={item.id} />
            </>
          ) : null}
        </div>
        {/* 원본 영상 크기 조정 핸들 — 크롭 프레임(위 레이어)이 아니라 원본 소스 박스
            꼭짓점에 붙인다. 바깥으로 끌면 확대, 안으로 끌면 축소(프레임은 고정). */}
        {itemIsBeingCropped && sourceBoxStyle ? (
          <div style={sourceBoxStyle}>
            <CropSourceScaleHandle
              itemId={item.id}
              corner="top-left"
            />
            <CropSourceScaleHandle
              itemId={item.id}
              corner="top-right"
            />
            <CropSourceScaleHandle
              itemId={item.id}
              corner="bottom-left"
            />
            <CropSourceScaleHandle
              itemId={item.id}
              corner="bottom-right"
            />
          </div>
        ) : null}
      </>
    ) : null;

  // 핸들 레이어만 최상단에서 렌더하는 pass. outline div · hover 히트영역 없이
  // pointerEvents:none 레이어 안의 핸들·크롭 버튼만 살아 있어 다른 아이템 클릭을 막지 않는다.
  if (phase === 'handles') {
    return handleLayerContent;
  }

  return (
    <ItemContextMenuTrigger item={item}>
      <div
        onPointerDown={onPointerDown}
        onPointerEnter={onMouseEnter}
        onPointerLeave={onMouseLeave}
        onDoubleClick={(e) => {
          /* 캡션은 여기서 Text 수정 불가 */
          if (isTextItem(item) && item.renderMeta?.clipType === 'caption') {
            return;
          }
          onDoubleClick(e);
        }}
        style={style}
        data-id={item.id}
      >
        {codeEditableFields.map((field) => {
          const isActive = codeActiveFieldKeySet.has(field.key);
          const isFocused = focusedCodeFieldKey === field.key;

          if (!field.geometry) {
            return null;
          }

          const fieldGeometry =
            draftCodeFieldGeometries?.[field.key] ?? field.geometry;
          const handleSize = Math.max(6, Math.round(8 / scale));

          return (
            <div
              key={field.key}
              style={{
                position: 'absolute',
                left: fieldGeometry.left,
                top: fieldGeometry.top,
                width: fieldGeometry.width,
                height: fieldGeometry.height,
                zIndex: 2,
              }}
            >
              {/* Canvas overlay hotspot: DS Button adds visible chrome/layout that breaks the absolute hit-area. */}
              <button
                type="button"
                aria-label={field.label}
                aria-pressed={isActive}
                disabled={isLocked}
                title={field.label}
                onPointerDown={(event) => {
                  startCodeFieldMove(field.key, fieldGeometry, event);
                }}
                onClick={(event) => {
                  event.stopPropagation();

                  if (event.detail === 0) {
                    selectCodeFields(
                      getNextCodeFieldSelection({
                        currentFieldKeys: codeActiveFieldKeys,
                        fieldKey: field.key,
                        multiSelect: event.metaKey || event.shiftKey,
                      }),
                    );
                  }
                }}
                onDoubleClick={(event) =>
                  startCodeFieldInlineEdit(field, event)
                }
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  appearance: 'none',
                  border: `1px dashed ${
                    isActive || isFocused
                      ? 'var(--color-editor-starter-accent)'
                      : 'transparent'
                  }`,
                  borderRadius: 6,
                  background: 'transparent',
                  boxShadow: isFocused
                    ? '0 0 0 2px var(--color-editor-starter-accent)'
                    : undefined,
                  cursor: isLocked ? 'not-allowed' : isActive ? 'move' : 'text',
                  outline: 'none',
                  padding: 0,
                }}
                onFocus={() => setFocusedCodeFieldKey(field.key)}
                onBlur={() => setFocusedCodeFieldKey(null)}
                onMouseEnter={(event) => {
                  event.currentTarget.style.borderColor =
                    'var(--color-editor-starter-accent)';
                }}
                onMouseLeave={(event) => {
                  if (!isActive && !isFocused) {
                    event.currentTarget.style.borderColor = 'transparent';
                  }
                }}
              />
              {editingCodeField?.fieldKey === field.key ? (
                <CodeFieldInlineEditor
                  inlineEditStyle={field.inlineEditStyle}
                  value={editingCodeField.initialValue}
                  onCancel={() => setEditingCodeField(null)}
                  onCommit={(nextValue) =>
                    commitCodeFieldText({
                      fieldKey: field.key,
                      initialValue: editingCodeField.initialValue,
                      nextValue,
                    })
                  }
                />
              ) : null}
              {isActive
                ? CODE_FIELD_HANDLE_TYPES.map((handleType) => (
                    <div
                      key={handleType}
                      aria-hidden="true"
                      onPointerDown={(event) =>
                        startCodeFieldResize(
                          field.key,
                          fieldGeometry,
                          handleType,
                          event,
                        )
                      }
                      onClick={(event) => event.stopPropagation()}
                      style={codeFieldHandleStyle(handleType, handleSize)}
                    />
                  ))
                : null}
            </div>
          );
        })}
        {uploadProgress ? (
          <SelectionUploadProgress uploadProgress={uploadProgress} />
        ) : null}
        {uploadError ? (
          <SelectionError
            uploadError={uploadError}
            onRetry={canRetry ? handleRetry : undefined}
            canRetry={Boolean(canRetry)}
          />
        ) : null}
      </div>
      {selected && ghostStyle ? <div style={ghostStyle} /> : null}
      {selected && motionPath ? (
        <svg
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            overflow: 'visible',
            pointerEvents: 'none',
            zIndex: 999,
          }}
        >
          <polyline
            points={motionPath.map((p) => `${p.x},${p.y}`).join(' ')}
            fill="none"
            stroke="var(--color-editor-starter-accent)"
            strokeWidth={2 / scale}
            strokeDasharray={`${6 / scale} ${4 / scale}`}
            opacity={0.7}
          />
          {motionPath.map((p, index) => (
            <circle
              key={`${p.x}-${p.y}-${index}`}
              cx={p.x}
              cy={p.y}
              r={4 / scale}
              fill="var(--color-editor-starter-accent)"
            />
          ))}
        </svg>
      ) : null}
      {phase === 'base' ? null : handleLayerContent}
    </ItemContextMenuTrigger>
  );
};

export const SelectionOutline = React.memo(SelectionOutlineUnmemoized);
